<template>
	<div class="api-tokens">
		<header>
			<BackToSettings />
			<h1>{{ $t('settings_option_api_tokens') }}</h1>
			<Button primary pill @click="newTokenClick">
				{{ $t('settings_apitokens_new_token') }}
			</Button>
		</header>

		<section :class="{ 'empty-state': tokens.length === 0 }">
			<div v-if="tokens.length > 0" class="list-help">
				{{ $t('settings_apitokens_list_help') }}
				<a :href="$t('common_apitoken_learnmore_url')" target="_blank">
					{{ $t('common_learn_more') }}
				</a>
			</div>

			<div class="retrieving-spinner-wrapper" v-if="retrieving_tokens">
				<SpinnerOverlay
					class="retrieving-spinner"
					cBackground="unset"
					sWidth="50px"
					sHeight="50px"
				/>
			</div>

			<NoTokens v-if="tokens.length === 0 && !retrieving_tokens" />
			<TokenList v-if="tokens.length > 0" @revoke="showRevoke" />
		</section>

		<NewToken
			v-if="modalState === NEW_TOKEN"
			:name="name"
			@cancel="changeModalState(null)"
			@next="onNewTokenNext($event)"
		/>

		<SelectScopes
			v-if="modalState === SELECT_SCOPES"
			:name="name"
			@cancel="changeModalState(null)"
			@back="changeModalState(NEW_TOKEN)"
			@next="onSelectScopesNext($event)"
		/>

		<ShowToken
			v-if="modalState === SHOW_TOKEN"
			:token="token"
			@next="onCancel"
			@cancel="changeModalState(null)"
		/>

		<RevokeToken
			v-if="modalState === REVOKE_TOKEN"
			:token="token"
			@cancel="onCancel"
		/>

		<MaxTokens
			v-if="modalState === MAX_TOKENS"
			@next="changeModalState(null)"
			@close="changeModalState(null)"
		/>
	</div>
</template>

<script>
import { Button } from '@/components/common'
import NoTokens from './NoTokens'
import TokenList from './TokenList'
import NewToken from './NewToken'
import SelectScopes from './SelectScopes'
import ShowToken from './ShowToken'
import RevokeToken from './RevokeToken'
import MaxTokens from './MaxTokens'
import SpinnerOverlay from '@/components/SpinnerOverlay'
import { getTokenHistory, getTokensByValues, activeTokens } from './apitokens'
import BackToSettings from '@/components/MainSettings/BackToSettings'

const states = {
	NEW_TOKEN: 1,
	SELECT_SCOPES: 2,
	SHOW_TOKEN: 3,
	REVOKE_TOKEN: 4,
	MAX_TOKENS: 5,
}

export default {
	name: 'APITokens',
	components: {
		Button,
		NoTokens,
		TokenList,
		NewToken,
		SelectScopes,
		ShowToken,
		RevokeToken,
		MaxTokens,
		SpinnerOverlay,
		BackToSettings,
	},

	data() {
		return {
			...states,
			name: '',
			scopes: [],
			token: null,
			modalState: null,
			retrieving_tokens: false,
		}
	},

	async created() {
		this.retrieving_tokens = true
		const { ok } = await getTokenHistory()
		if (!ok) {
			this.$alert.error()
		}
		this.retrieving_tokens = false
	},

	computed: {
		tokens() {
			return getTokensByValues()
		},
	},

	methods: {
		changeModalState(state) {
			this.modalState = state
		},

		newTokenClick() {
			// reset any previous state
			this.name = null
			this.scopes = []
			this.token = null
			// open appropriate modal
			const isAtMax = activeTokens().length >= 10
			this.changeModalState(isAtMax ? states.MAX_TOKENS : states.NEW_TOKEN)
		},

		showRevoke(token) {
			this.token = token
			this.changeModalState(states.REVOKE_TOKEN)
		},

		onNewTokenNext($event) {
			this.name = $event
			this.changeModalState(states.SELECT_SCOPES)
		},

		onSelectScopesNext(token) {
			this.token = token
			this.changeModalState(states.SHOW_TOKEN)
		},

		onCancel() {
			this.token = null
			this.changeModalState(null)
		},
	},
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.api-tokens {
	$header-size: 60px;
	grid-area: header / header / main / main;

	header {
		height: $header-size;
		padding: 0 32px 0 0;
		border-bottom: 1px solid var(--neutral-40040);
		display: flex;
		align-items: center;
		justify-content: space-between;

		> .pronto-btn {
			position: static !important;
		}

		h1 {
			font-family: 'Avenir-Heavy';
			font-size: 20px;
			color: var(--neutral-800);
		}
	}

	section {
		height: 100%;
		position: relative;
		padding-top: 24px;
		padding-bottom: 0;
		max-height: calc(100% - #{$header-size});
		overflow-y: auto;
	}

	.back {
		display: flex;
		align-items: center;
		.chevron {
			margin-right: 15px;
		}
	}

	.token-modal {
		padding: 30px;
	}

	.list-help {
		font-family: 'Avenir-Medium';
		font-size: 14px;
		color: var(--neutral-800);
		margin: 0 0 25px 25px;
	}

	.retrieving-spinner-wrapper {
		display: flex;
		justify-content: center;
		align-items: center;
		height: 100%;
		width: 100%;
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.api-tokens section .list-help a,
.api-tokens section .list-help a:visited {
	font-family: 'Avenir-Heavy';
	color: var(--blue-300);
	text-decoration: none;
}
</style>

<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.retrieving-spinner {
		.spinner-div,
		.ios-spinner {
			background-color: var(--neutral-100) !important;
		}
	}
}
</style>
